import React, { type ReactNode } from 'react';
import { styled, css } from '@compiled/react';
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { isVisualRegressionTest } from '@atlassian/jira-platform-test-utils-visual-regression/src/common/utils.tsx';
import type { Deployment } from '../../../../../common/types';
import DeploymentIcon from '../../../../../common/ui/deployment-icon';
import { type Direction, Directions } from '../../../../../controllers/deployments';

export type Props = {
	show: boolean;
	direction: Direction;
	deployment?: Deployment;
	onClick: () => void;
};

export const NavigateIndicator = ({ show, direction, deployment, onClick }: Props) => {
	const isFlipped = direction === Directions.RIGHT;
	return (
		<Container
			// add classname for VR test waitForScreenshot element
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={show ? 'show' : undefined}
			show={show}
			isFlipped={isFlipped}
			onClick={onClick}
			data-testid="software-deployments.ui.timeline.chart-indicators.navigate-indicators.navigate-indicator.container"
		>
			<ArrowContainer isFlipped={isFlipped}>
				{direction === Directions.LEFT && <ChevronLeftIcon label="" />}
				{direction === Directions.RIGHT && <ChevronRightIcon label="" />}
			</ArrowContainer>
			{deployment && (
				<IconContainer isFlipped={isFlipped}>
					<DeploymentIcon
						deployments={[deployment]}
						showMultipleDeploymentsIcon={false}
						hasBorder={false}
					/>
				</IconContainer>
			)}
		</Container>
	);
};

type ContainerProps = {
	isFlipped: boolean;
	show: boolean;
	children: ReactNode;
	className?: string;
	['data-testid']: string;
	onClick: () => void;
};

const flexCenterStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const animateInStyles = css({
	opacity: 1,
	transform: 'translate3d(0px, 0, 0)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<ContainerProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 6}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 4}px`,
		display: 'flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('elevation.surface.raised', colors.N0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		boxShadow: token('elevation.shadow.raised', `0 0 1px ${colors.N60A}, 0 1px 1px ${colors.N50A}`),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		borderRadius: `${gridSize * 2}px`,
		cursor: 'pointer',
		opacity: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		transition: isVisualRegressionTest
			? 'none'
			: 'opacity 200ms ease-in-out, transform 200ms ease-out',
		transform: 'translate3d(20px, 0, 0)',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		flexDirection: ({ isFlipped }: ContainerProps) => (isFlipped ? 'row-reverse' : 'row'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ show }: ContainerProps) => (show ? animateInStyles : {}),
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		pointerEvents: ({ show }: ContainerProps) => (show ? 'auto' : 'none'),
	},
);

interface IconArrowProps {
	isFlipped: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const IconContainer = styled.div<IconArrowProps>(flexCenterStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ isFlipped }: IconArrowProps) =>
		isFlipped ? token('space.050', '4px') : token('space.negative.050', '-4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ArrowContainer = styled.div<IconArrowProps>(flexCenterStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: ({ isFlipped }: IconArrowProps) => (isFlipped ? '0px' : token('space.050', '4px')),
});

export default NavigateIndicator;
